import { useState, useRef, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d6t9sqkj8d20juc8vvng';

const contactInfo = [
  {
    icon: 'ri-map-pin-line',
    label: 'Address',
    value: 'Donja Mahala 5, Visoko 71300, Bosnia and Herzegovina',
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    value: 'contact@krokodoo.com',
  },
  {
    icon: 'ri-phone-line',
    label: 'Phone',
    value: '+1 8382700220',
  },
];

export default function ContactSection() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageEl = form.elements.namedItem('message') as HTMLTextAreaElement;
    if (messageEl && messageEl.value.length > 500) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={leftRef}
          className={`text-center mb-16 animate-fade-up ${leftVisible ? 'visible' : ''}`}
        >
          <span className="text-[#C74B1C] text-sm font-semibold tracking-[0.2em] uppercase">
            Contact Us
          </span>
          <h2
            className="mt-3 font-extrabold text-[#0D0D0D]"
            style={{ fontFamily: 'Syne, sans-serif', fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            Get in Touch with KROKO
          </h2>
          <p className="mt-4 text-[#6B6B6B] text-base max-w-xl mx-auto leading-relaxed">
            Have a question or want to work with us? We&apos;re happy to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-14">
          {/* Contact Info */}
          <div
            ref={leftRef}
            className={`animate-slide-left ${leftVisible ? 'visible' : ''}`}
          >
            <div className="bg-[#0D0D0D] rounded-2xl p-10 h-full flex flex-col justify-between">
              <div>
                <h3
                  className="text-white font-bold text-xl mb-8"
                  style={{ fontFamily: 'Syne, sans-serif' }}
                >
                  Contact Information
                </h3>
                <div className="flex flex-col gap-7">
                  {contactInfo.map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 shrink-0">
                        <i className={`${item.icon} text-[#C74B1C] text-base`} />
                      </div>
                      <div>
                        <div className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-1">
                          {item.label}
                        </div>
                        <div className="text-white text-sm leading-relaxed">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className="mt-10 rounded-xl overflow-hidden h-36">
                <img
                  src="https://public.readdy.ai/ai/img_res/ce47fd86e62a7bd83e495fbeb9b677c4.jpg"
                  alt="Visoko, Bosnia and Herzegovina location"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Form */}
          <div
            ref={rightRef}
            className={`animate-slide-right ${rightVisible ? 'visible' : ''}`}
          >
            <form
              ref={formRef}
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-[#3A3A3A] uppercase tracking-widest mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-3 text-sm bg-[#F5F3EE] border border-stone-200 rounded-lg focus:outline-none focus:border-[#C74B1C] text-[#0D0D0D] placeholder-[#AAAAAA] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#3A3A3A] uppercase tracking-widest mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 text-sm bg-[#F5F3EE] border border-stone-200 rounded-lg focus:outline-none focus:border-[#C74B1C] text-[#0D0D0D] placeholder-[#AAAAAA] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#3A3A3A] uppercase tracking-widest mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="w-full px-4 py-3 text-sm bg-[#F5F3EE] border border-stone-200 rounded-lg focus:outline-none focus:border-[#C74B1C] text-[#0D0D0D] placeholder-[#AAAAAA] transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#3A3A3A] uppercase tracking-widest mb-2">
                  Message
                  <span className={`ml-2 font-normal normal-case tracking-normal ${charCount > 480 ? 'text-[#C74B1C]' : 'text-[#AAAAAA]'}`}>
                    {charCount}/500
                  </span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm bg-[#F5F3EE] border border-stone-200 rounded-lg focus:outline-none focus:border-[#C74B1C] text-[#0D0D0D] placeholder-[#AAAAAA] transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-3.5 bg-[#C74B1C] text-white text-sm font-semibold rounded-lg hover:bg-[#A63B14] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <div className="flex items-center gap-2 text-[#2D6A4F] bg-[#EDFBF5] border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-sm" />
                  </div>
                  <span>Message sent! We&apos;ll get back to you soon.</span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-[#C74B1C] bg-[#FDF1EC] border border-[#C74B1C]/20 rounded-lg px-4 py-3 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-error-warning-line text-sm" />
                  </div>
                  <span>Something went wrong. Please try again or email us directly.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
